import axios, { AxiosError } from 'axios';

/**
 * API 根地址。**唯一真相源** —— 走 axios 的地方用下面的 `api`，需要裸 fetch 的地方
 * （SSE 长连接、文件字节流这类非 JSON 响应）用这个函数拼地址，别各写一份回落值。
 *
 * dev 下留空走 vite 代理（同源、无预检）；生产由 .env 的 VITE_API_BASE_URL 给绝对地址。
 */
export function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

/** localStorage 里的会话令牌 key。裸 fetch 时要自己带 Bearer（拦截器只作用于 axios）。 */
export const TOKEN_KEY = 'hipay_token';

/** 全局 HTTP 客户端。所有接口一律 POST /api/<资源>/<动作>，参数走 JSON body。 */
export const api = axios.create({ baseURL: apiBase() });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * 把 Blob 形态的错误体还原成 JSON（就地改写 `error.response.data`）。
 *
 * 🔴 `responseType: 'blob'` 的请求（确认函 PDF）**出错时错误体也是 Blob**。不还原的话
 * `apiErrorCode` / `apiErrorMessage` 读到的是一个 Blob 对象，取不出短码与文案 ——
 * 表现是确认函取不到时只弹一句笼统的兜底话，而服务端明明区分了「取不到，去 Airwallex
 * 后台下载」和「钱没动，压根不存在确认函」两句。这个仓库里这类区别是不能丢的。
 *
 * 解析失败（网关回的 HTML、空体）不抛：那时本来就没有结构化信息可用，
 * 交给调用方的兜底文案即可。
 */
export async function normalizeBlobError(error: unknown): Promise<void> {
  const res = (error as AxiosError)?.response;
  if (!res || !(res.data instanceof Blob)) return;
  try {
    const text = await res.data.text();
    res.data = JSON.parse(text);
  } catch {
    // 不是 JSON 就保持原样，上层会用兜底文案
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    // 🔴 必须在读 code 之前还原：blob 请求的错误体是 Blob，不还原就什么都读不出来
    await normalizeBlobError(error);
    const status = error.response?.status;
    const code = apiErrorCode(error);
    // 🔴 401 不能笼统当「会话失效」：登录接口本身在凭证错误时也回 401，那跟当前 session
    // 有没有失效毫无关系。后端的鉴权中间件恒定打 UNAUTHENTICATED，只有这个短码代表
    // 「你的登录状态死了」，必须精确匹配，否则一次失败的登录尝试会把已登录用户踢下线。
    if (status === 401 && code === 'UNAUTHENTICATED') {
      localStorage.removeItem(TOKEN_KEY);
      if (location.pathname !== '/login') location.href = '/login';
    }
    return Promise.reject(error);
  },
);

/** 后端错误信封恒为 `{ error: { code, message } }`（见 backend/src/shared/plugins/error.ts）。 */
interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    /** Airwallex 校验失败时的出错字段 path 列表，动态表单靠它把红字标到具体那一格。 */
    fields?: string[];
    airwallexCode?: string;
    clientRowId?: string;
    rowNumber?: number;
    requestId?: string;
    fieldErrors?: Array<{ source: string; code?: string }>;
  };
}

export function apiErrorCode(err: unknown): string | undefined {
  return ((err as AxiosError<ApiErrorBody>)?.response?.data)?.error?.code;
}

export function apiErrorMessage(err: unknown, fallback = '请求失败，请稍后重试'): string {
  return ((err as AxiosError<ApiErrorBody>)?.response?.data)?.error?.message || fallback;
}

/**
 * Airwallex 校验失败时，出错字段的 path 列表。
 * 服务端把 `details.errors[].source` 原样透传过来（见 backend/src/shared/airwallex/client.ts）——
 * 丢掉它，用户只会看到一句「校验失败」而不知道该改哪一格。
 */
export function apiErrorFields(err: unknown): string[] {
  return ((err as AxiosError<ApiErrorBody>)?.response?.data)?.error?.fields ?? [];
}

/** 批量提交失败时的行定位与脱敏诊断信息。 */
export function apiBatchRowError(err: unknown) {
  return ((err as AxiosError<ApiErrorBody>)?.response?.data)?.error;
}
